import { FC, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { UploadFile, UploadProps } from 'antd';
import { Upload } from 'antd';

import { bytesToMb, formatFileSize } from 'utils/formatFileSize';
import { notify } from 'utils/toast/createToast';

import { AlertType } from 'constants/enums';
import { cvFileTypes } from 'constants/jobseekerSettings';

import { ReactComponent as FileIcon } from 'assets/icons/file.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';

interface UploadFileControlProps {
  onChangeFile: (file: UploadFile | null) => void;
  isRoundCorners?: boolean;
  allowedFileTypes?: string;
  label?: string;
  maxFileSizeMB?: number;
}

const UploadFileControl: FC<UploadFileControlProps> = ({
  onChangeFile,
  isRoundCorners,
  allowedFileTypes = cvFileTypes,
  label,
  maxFileSizeMB
}): ReactElement => {
  const { t } = useTranslation();

  const { Dragger } = Upload;
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const uploadLable = label ?? t('common.uploadFile.label');

  const handleBeforeUpload: UploadProps['beforeUpload'] = (file) => {
    const isFileUploadSizeExceeded =
      maxFileSizeMB && file?.size && bytesToMb(file.size) > maxFileSizeMB;

    if (isFileUploadSizeExceeded) {
      notify({
        type: AlertType.Error,
        message: t('upload.fileSizeError', {
          maxSize: maxFileSizeMB,
          currentSize: formatFileSize(file?.size ?? 0)
        })
      });
      return Upload.LIST_IGNORE;
    }

    setFileList([file]);
    onChangeFile(file);
    return false;
  };

  const handleRemove = (): void => {
    setFileList([]);
    onChangeFile(null);
  };

  return (
    <div className="upload-button-container">
      <Dragger
        style={{ borderRadius: isRoundCorners ? '18px' : 0 }}
        fileList={fileList}
        beforeUpload={handleBeforeUpload}
        accept={allowedFileTypes}
        maxCount={1}
        onRemove={handleRemove}
        iconRender={() => <FileIcon />}
        showUploadList={{
          showRemoveIcon: true,
          removeIcon: <TrashIcon className="icon-trash" />
        }}
      >
        <UploadIcon />
        <span>{uploadLable}</span>
      </Dragger>
    </div>
  );
};

export default UploadFileControl;
